import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiGet, apiPost } from "../../utils/api";
import { displayName } from "../../utils/displayName";
import { toast } from "../../utils/toast";
import { useTeamFilter } from "../../utils/useTeamFilter";
import { Globe, ArrowLeft, RefreshCw, User, ShieldAlert, ShieldCheck, Clock, Eye, Monitor, Users, Search, ChevronLeft, ChevronRight } from "lucide-react";
import "./urltracker.css";
import "../devices/devices.css";

const LOGS_PAGE_SIZE = 25;

const UrlTracker = () => {
    const navigate = useNavigate();
    const [agents, setAgents] = useState([]);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [urlLogs, setUrlLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [logsLoading, setLogsLoading] = useState(false);
    const [rechecking, setRechecking] = useState(false);
    const { departments, getTeamName } = useTeamFilter();
    const [selectedTeam, setSelectedTeam] = useState("all");
    const [searchTerm, setSearchTerm] = useState("");
    const [logsPage, setLogsPage] = useState(1);

    const handleRecheckAll = async () => {
        try {
            setRechecking(true);
            const response = await apiPost("/api/url-tracker/recheck");
            if (response.ok) {
                const data = await response.json();
                toast.success(data.message || "Recheck completed successfully");
                if (selectedAgent) {
                    fetchAgentLogs(selectedAgent);
                } else {
                    fetchAgents();
                }
            } else {
                const errorData = await response.json().catch(() => ({}));
                toast.error(errorData.message || "Failed to recheck domains");
            }
        } catch (err) {
            console.error("Failed to recheck domains:", err);
            toast.error("Failed to recheck domains");
        } finally {
            setRechecking(false);
        }
    };

    useEffect(() => {
        fetchAgents();
    }, []);

    const fetchAgents = async () => {
        try {
            setLoading(true);
            const response = await apiGet("/api/url-tracker/agents");
            if (response.ok) {
                const data = await response.json();
                setAgents(data.agents || []);
            } else {
                toast.error("Failed to fetch agents");
            }
        } catch (err) {
            console.error("Failed to fetch agents:", err);
            toast.error("Failed to fetch agents");
        } finally {
            setLoading(false);
        }
    };

    const fetchAgentLogs = async (agentId) => {
        try {
            setLogsLoading(true);
            const response = await apiGet(`/api/url-tracker/${agentId}`);
            if (response.ok) {
                const data = await response.json();
                setUrlLogs(data || []);
            } else {
                toast.error("Failed to fetch URL activity");
            }
        } catch (err) {
            console.error("Failed to fetch URL activity:", err);
            toast.error("Failed to fetch URL activity");
        } finally {
            setLogsLoading(false);
        }
    };

    const handleAgentClick = (agentId) => {
        setSelectedAgent(agentId);
        setLogsPage(1);
        fetchAgentLogs(agentId);
    };

    const handleBack = () => {
        setSelectedAgent(null);
        setUrlLogs([]);
        setLogsPage(1);
    };

    // Format duration (seconds) to human readable
    const formatDuration = (seconds) => {
        if (!seconds || seconds < 1) return "< 1s";
        if (seconds < 60) return `${Math.round(seconds)}s`;
        if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return `${hours}h ${mins}m`;
    };

    // Get browser icon name
    const getBrowserName = (appName) => {
        if (!appName) return "Unknown";
        const name = appName.replace('.exe', '').toLowerCase();
        const browserMap = {
            'chrome': 'Chrome',
            'msedge': 'Edge',
            'firefox': 'Firefox',
            'brave': 'Brave',
            'opera': 'Opera',
            'safari': 'Safari'
        };
        return browserMap[name] || name.charAt(0).toUpperCase() + name.slice(1);
    };

    // Calculate stats
    const getStats = () => {
        const totalVisits = urlLogs.reduce((sum, log) => sum + (log.visitCount || 0), 0);
        const totalTime = urlLogs.reduce((sum, log) => sum + (parseFloat(log.totalDuration) || 0), 0);
        const threats = urlLogs.filter(log => log.alert === "THREAT_DETECTED").length;
        return { totalVisits, totalTime, threats, domains: urlLogs.length };
    };

    // Agent List View
    if (!selectedAgent) {
        const filteredAgents = agents
            .filter((agent) => selectedTeam === "all" || getTeamName(agent.agentId || agent) === selectedTeam)
            .filter((agent) => displayName(agent).toLowerCase().includes(searchTerm.trim().toLowerCase()));

        return (
            <div className="urltracker-wrapper">
                <div className="urltracker-header">
                    <p className="urltracker-subtitle">
                        Select an agent to view their web browsing activity
                    </p>
                    <button
                        className="gc-icon-btn"
                        data-tip={rechecking ? "Rechecking…" : "Recheck All Domains"}
                        onClick={handleRecheckAll}
                        disabled={rechecking || loading}
                    >
                        {rechecking ? <RefreshCw size={14} className="spin" /> : <ShieldAlert size={14} />}
                    </button>
                    <button className="gc-icon-btn" data-tip="Refresh" onClick={fetchAgents} disabled={rechecking || loading}>
                        <RefreshCw size={14} />
                    </button>
                </div>

                <div className="team-filter-row">
                    <div className="name-search-wrapper">
                        <Search size={16} className="team-filter-icon" />
                        <input
                            type="text"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {departments.length > 0 && (
                        <div className="team-select-wrapper">
                            <Users size={16} className="team-filter-icon" />
                            <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
                                <option value="all">All Teams</option>
                                {departments.map((dept) => (
                                    <option key={dept._id} value={dept.name}>{dept.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="loading-state">Loading agents...</div>
                ) : filteredAgents.length === 0 ? (
                    <div className="empty-state">
                        <Globe size={48} />
                        <p>No agents with web activity found</p>
                        <small>Run the URL Tracker on agents to generate data</small>
                    </div>
                ) : (
                    <div className="agents-grid">
                        {filteredAgents.map((agent, idx) => (
                            <div
                                key={idx}
                                className="agent-card"
                                onClick={() => handleAgentClick(agent.agentId || agent)}
                            >
                                <div className="agent-icon">
                                    <User size={24} />
                                </div>
                                <div className="agent-info">
                                    <h3>{displayName(agent)}</h3>
                                    <div className="agent-counts">
                                        <span style={{ color: '#00bfff' }}>
                                            {agent.domains || 0} URLs
                                        </span>
                                        <span style={{ color: '#ffc107', marginLeft: '12px' }}>
                                            {formatDuration(agent.totalTime || 0)} Time
                                        </span>
                                        <span style={{ color: '#ff5757', marginLeft: '12px' }}>
                                            {agent.threats || 0} Threat
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    const stats = getStats();
    const logsTotalPages = Math.max(1, Math.ceil(urlLogs.length / LOGS_PAGE_SIZE));
    const pagedLogs = urlLogs.slice((logsPage - 1) * LOGS_PAGE_SIZE, logsPage * LOGS_PAGE_SIZE);

    // Detail View
    return (
        <div className="urltracker-wrapper">
            <div className="urltracker-header">
                <button className="gc-icon-btn" onClick={handleBack} disabled={rechecking || logsLoading} data-tip="Back to Agents" aria-label="Back to Agents">
                    <ArrowLeft size={18} />
                </button>
                <h1 className="urltracker-title">
                    <Globe size={24} />
                    {selectedAgent}
                </h1>
                <button
                    className="gc-icon-btn"
                    data-tip={rechecking ? "Rechecking…" : "Recheck All Domains"}
                    onClick={handleRecheckAll}
                    disabled={rechecking || logsLoading}
                >
                    {rechecking ? <RefreshCw size={14} className="spin" /> : <ShieldAlert size={14} />}
                </button>
                <button className="gc-icon-btn" data-tip="Refresh" onClick={() => fetchAgentLogs(selectedAgent)} disabled={rechecking || logsLoading}>
                    <RefreshCw size={14} />
                </button>
            </div>

            {/* Stats Cards */}
            <div className="url-stats-grid">
                <div className="url-stat-card">
                    <div className="stat-icon domains">
                        <Globe size={20} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{stats.domains}</span>
                        <span className="stat-label">Unique Domains</span>
                    </div>
                </div>
                <div className="url-stat-card">
                    <div className="stat-icon visits">
                        <Eye size={20} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{stats.totalVisits}</span>
                        <span className="stat-label">Total Visits</span>
                    </div>
                </div>
                <div className="url-stat-card">
                    <div className="stat-icon time">
                        <Clock size={20} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-value">{formatDuration(stats.totalTime)}</span>
                        <span className="stat-label">Total Time</span>
                    </div>
                </div>
                <div className="url-stat-card">
                    <div className={`stat-icon ${stats.threats > 0 ? 'threats-active' : 'threats'}`}>
                        <ShieldAlert size={20} />
                    </div>
                    <div className="stat-info">
                        <span className={`stat-value ${stats.threats > 0 ? 'threat-value' : ''}`}>{stats.threats}</span>
                        <span className="stat-label">Threats Detected</span>
                    </div>
                </div>
            </div>

            <div className="urltracker-content">
                {logsLoading ? (
                    <div className="loading-state">Loading activity...</div>
                ) : urlLogs.length === 0 ? (
                    <div className="empty-state">
                        <Globe size={48} />
                        <p>No URL activity found</p>
                        <small>Activity will appear here as the agent browses</small>
                    </div>
                ) : (
                    <table className="url-table">
                        <thead>
                            <tr>
                                <th>Domain</th>
                                <th>Page Title</th>
                                <th>Visits</th>
                                <th>Total Duration</th>
                                <th>Browser</th>
                                <th>Status</th>
                                <th>First Visited</th>
                                <th>Last Visited</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pagedLogs.map((log, i) => (
                                <tr key={i} className={log.alert === "THREAT_DETECTED" ? "threat-row" : ""}>
                                    <td className="url-domain">{log.url}</td>
                                    <td className="title-cell" title={log.title || "N/A"}>
                                        {log.title ? (log.title.length > 40 ? log.title.substring(0, 40) + '...' : log.title) : '-'}
                                    </td>
                                    <td className="visits-cell">{log.visitCount || 1}</td>
                                    <td className="duration-cell">{formatDuration(parseFloat(log.totalDuration) || 0)}</td>
                                    <td className="browser-cell">
                                        <span className="browser-tag">
                                            <Monitor size={14} />
                                            {getBrowserName(log.app)}
                                        </span>
                                    </td>
                                    <td>
                                        {log.alert === "THREAT_DETECTED" ? (
                                            <span className="threat-badge threat-detected">
                                                <ShieldAlert size={12} />
                                                Threat
                                            </span>
                                        ) : (
                                            <span className="threat-badge threat-clean">
                                                <ShieldCheck size={12} />
                                                Clean
                                            </span>
                                        )}
                                    </td>
                                    <td className="timestamp-cell">
                                        {log.firstVisited ? new Date(log.firstVisited).toLocaleString() : '-'}
                                    </td>
                                    <td className="timestamp-cell">
                                        {new Date(log.lastVisted || log.updatedAt || log.timestamp).toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
                {urlLogs.length > 0 && (
                    <div className="devices-pagination">
                        <span className="devices-pagination-count">
                            {(logsPage - 1) * LOGS_PAGE_SIZE + 1}–{Math.min(logsPage * LOGS_PAGE_SIZE, urlLogs.length)} of {urlLogs.length} domain{urlLogs.length !== 1 ? "s" : ""}
                        </span>
                        <div className="devices-pagination-controls">
                            <button
                                className="gc-icon-btn"
                                data-tip="Previous page"
                                onClick={() => setLogsPage((p) => Math.max(1, p - 1))}
                                disabled={logsPage <= 1}
                            >
                                <ChevronLeft size={15} />
                            </button>
                            <span className="devices-pagination-page">{logsPage} / {logsTotalPages}</span>
                            <button
                                className="gc-icon-btn"
                                data-tip="Next page"
                                onClick={() => setLogsPage((p) => Math.min(logsTotalPages, p + 1))}
                                disabled={logsPage >= logsTotalPages}
                            >
                                <ChevronRight size={15} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <p className="url-footer">
                Showing {urlLogs.length} unique domains • {stats.totalVisits} total visits
            </p>
        </div>
    );
};

export default UrlTracker;
